// Rehearsal Room — port of Ryan's MagicPatterns RehearsalRoomScreen.tsx
// (vanilla, no framer-motion). Four rooms ride a horizontal track under
// the Presence orb; the armed room sits below the center pointer-dot;
// swipe steps, tap the dot enters the armed room.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Annotation, getAnnotations, postAnalyze, postAnnotation } from "../api/client";
import { useArc } from "../state/arc";

type Room = "Drills" | "Run Lines" | "Stats" | "Journal";
const ROOMS: Room[] = ["Drills", "Run Lines", "Stats", "Journal"];
const ROOM_DESC: Record<Room, string> = {
  "Drills":    "Targeted memory drills",
  "Run Lines": "Rehearse the scene with the Presence",
  "Stats":     "Your progress, off book",
  "Journal":   "Your script & methodology",
};
const ITEM_WIDTH = 150;
const SWIPE_THRESHOLD = 45;

export default function RehearsalRoom() {
  const navigate = useNavigate();
  const { state } = useArc();
  const [armedIdx, setArmedIdx] = useState(1);  // Default: Run Lines
  const [isDragging, setIsDragging] = useState(false);
  const [entering, setEntering] = useState(false);
  const [openRoom, setOpenRoom] = useState<Room | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartT = useRef<number>(0);

  function step(dir: number) {
    setArmedIdx((prev) => Math.min(ROOMS.length - 1, Math.max(0, prev + dir)));
  }

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    touchStartT.current = performance.now();
    setIsDragging(true);
  }
  function onTouchEnd(e: React.TouchEvent) {
    setIsDragging(false);
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dt = performance.now() - touchStartT.current;
    const v = Math.abs(dx) / Math.max(1, dt) * 1000;
    if (dx < -SWIPE_THRESHOLD || (dx < -10 && v > 350)) step(1);
    else if (dx > SWIPE_THRESHOLD || (dx > 10 && v > 350)) step(-1);
    touchStartX.current = null;
  }

  function handleEnter() {
    setEntering(true);
    const room = ROOMS[armedIdx];
    window.setTimeout(() => {
      setEntering(false);
      if (room === "Run Lines") navigate("/record");
      else setOpenRoom(room);
    }, 600);
  }

  // Derive Act/Scene tag from the parsed script — actor expects "ACT II SC 4" style.
  const actScene = useMemo(() => {
    const name = state.parsedScript?.scenes?.[0]?.name?.toUpperCase() ?? "";
    const m = name.match(/(ACT\s+\w+)(?:\s*[—-]\s*SC\s*(\d+))?/i);
    return m ? m[0] : name.slice(0, 18);
  }, [state.parsedScript]);

  if (!state.parsedProject) {
    return (
      <div className="v2">
        <p className="v2-tracked-caps" style={{ textAlign: "center" }}>Rehearsal room</p>
        <p className="v2-prompt" style={{ textAlign: "center", marginTop: "2rem" }}>
          Pick a script from the library first.
        </p>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
          <button className="v2-affordance" onClick={() => navigate("/library")}>Library</button>
        </div>
      </div>
    );
  }

  if (openRoom === "Drills")  return <DrillsRoom onBack={() => setOpenRoom(null)} />;
  if (openRoom === "Stats")   return <StatsRoom  onBack={() => setOpenRoom(null)} />;
  if (openRoom === "Journal") return <JournalRoom onBack={() => setOpenRoom(null)} />;

  const armedRoom = ROOMS[armedIdx];

  return (
    <div className="v2" style={{ display: "flex", flexDirection: "column", minHeight: "80vh", padding: "1rem" }}>
      {/* Header */}
      <div className="v2-rr-header">
        <div className="v2-rr-header-titles">
          <span className="v2-rr-script-title">{state.parsedProject.script_name}</span>
          {state.selectedCharacter && <span className="v2-rr-character">{state.selectedCharacter}</span>}
        </div>
        {actScene && <span className="v2-rr-actscene">{actScene}</span>}
      </div>

      {/* Presence orb */}
      <div className="v2-rr-orb-area">
        <div
          className="v2-presence"
          style={{
            transform: `scale(${entering ? 1.06 : isDragging ? 0.94 : 1})`,
            opacity: isDragging ? 0.8 : 1,
            transition: "transform 500ms cubic-bezier(0.22,1,0.36,1), opacity 320ms ease",
          }}
        />
      </div>

      {/* The arch navigator */}
      <div className="v2-arch" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="v2-arch-baseline" aria-hidden />

        <button
          className="v2-arch-pointer"
          onPointerUp={(e) => { e.stopPropagation(); handleEnter(); }}
          aria-label={`Enter ${armedRoom}`}
        >
          <span className="v2-arch-pointer-halo" aria-hidden />
          <span className="v2-arch-pointer-dot" aria-hidden />
          <span className="v2-arch-pointer-tick" aria-hidden />
        </button>

        <button
          className={"v2-arch-chev is-left" + (armedIdx === 0 ? " is-disabled" : "")}
          onPointerUp={(e) => { e.stopPropagation(); step(-1); }}
          aria-label="Previous room"
        >‹</button>
        <button
          className={"v2-arch-chev is-right" + (armedIdx === ROOMS.length - 1 ? " is-disabled" : "")}
          onPointerUp={(e) => { e.stopPropagation(); step(1); }}
          aria-label="Next room"
        >›</button>

        <div className="v2-arch-track-window">
          <div
            className="v2-arch-track"
            style={{
              marginLeft: `calc(50% - ${ITEM_WIDTH / 2}px)`,
              transform: `translateX(${-(armedIdx * ITEM_WIDTH)}px)`,
            }}
          >
            {ROOMS.map((room, i) => {
              const isArmed = i === armedIdx;
              const distance = Math.abs(i - armedIdx);
              const cls = isArmed ? "is-armed" : distance === 1 ? "is-near" : "is-far";
              return (
                <button
                  key={room}
                  className="v2-arch-slot"
                  onPointerUp={(e) => {
                    if (!isArmed && !isDragging) {
                      e.stopPropagation();
                      setArmedIdx(i);
                    }
                  }}
                >
                  <span className={`v2-arch-label ${cls}`}>{room.toUpperCase()}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="v2-arch-descriptor">
          <span
            className="v2-arch-descriptor-text"
            style={{ opacity: entering ? 0.4 : 1 }}
            key={armedIdx}
          >
            {entering ? `Entering ${armedRoom}…` : ROOM_DESC[armedRoom]}
          </span>
          <span className="v2-arch-hint">Swipe to choose · tap to enter</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Sub-rooms ----------

function DrillsRoom({ onBack }: { onBack: () => void }) {
  return (
    <div className="v2">
      <p className="v2-tracked-caps" style={{ textAlign: "center" }}>Drills</p>
      <h2 style={{ marginTop: 0, fontStyle: "italic", fontWeight: 300, textAlign: "center" }}>
        Targeted memory drills.
      </h2>
      <hr className="v2-hairline" />
      <p className="v2-prompt" style={{ textAlign: "center", maxWidth: "32rem", margin: "0 auto" }}>
        Read-through · cast upload · cue-line gap · prompt-the-word — these
        land here next. For now, Run Lines is the through-line.
      </p>
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <button className="v2-affordance" onClick={onBack}>Back</button>
      </div>
    </div>
  );
}

function StatsRoom({ onBack }: { onBack: () => void }) {
  const { state, update } = useArc();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function fire() {
    if (!state.parsedProject || !state.selectedCharacter || !state.methodologyId) {
      setError("Need a character before the analyzer runs.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const analysis = await postAnalyze({
        project: state.parsedProject,
        methodology_id: state.methodologyId,
        selected_characters: [state.selectedCharacter],
      });
      update({ analysis });
    } catch (e: any) {
      setError(e?.message ?? "Analyzer failed.");
    } finally {
      setBusy(false);
    }
  }

  const a = state.analysis;
  return (
    <div className="v2">
      <p className="v2-tracked-caps" style={{ textAlign: "center" }}>Stats</p>
      <h2 style={{ marginTop: 0, fontStyle: "italic", fontWeight: 300, textAlign: "center" }}>
        Your progress, off book.
      </h2>
      <hr className="v2-hairline" />

      {a && a.scene_analyses ? (
        <>
          <p className="v2-prompt" style={{ textAlign: "center" }}>
            {a.scene_analyses.length} scene{a.scene_analyses.length === 1 ? "" : "s"} read.{" "}
            {a.scene_analyses.reduce((s: number, x: any) => s + (x.cue_analysis?.length || 0), 0)} cues analyzed.
          </p>
          {a.arc?.characters?.find((c: any) => c.character === state.selectedCharacter) && (() => {
            const me = a.arc!.characters.find((c: any) => c.character === state.selectedCharacter)!;
            return (
              <>
                <hr className="v2-hairline" />
                <p className="v2-tracked-caps">Your arc</p>
                <p className="v2-prompt">{me.arc_summary}</p>
                {me.actor_notes && (
                  <>
                    <p className="v2-tracked-caps" style={{ marginTop: "1.2rem" }}>The questions</p>
                    <p className="v2-prompt">{me.actor_notes}</p>
                  </>
                )}
              </>
            );
          })()}
        </>
      ) : (
        <>
          <p className="v2-prompt" style={{ textAlign: "center" }}>
            The analyzer hasn't read the script yet. ~10 minutes for a full
            play; gives back beats, register shifts, and questions for the
            arc.
          </p>
          {busy && (
            <>
              <div className="v2-presence-small" aria-hidden style={{ marginTop: "1rem" }} />
              <p className="v2-stage-event" style={{ textAlign: "center" }}>Reading the script</p>
            </>
          )}
          {error && (
            <p className="v2-stage-event-detail" style={{ color: "#B83E3E", textAlign: "center", marginTop: "1rem" }}>
              {error}
            </p>
          )}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
            <button className="v2-affordance" onClick={fire} disabled={busy}>
              {busy ? "Reading…" : "Read the script"}
            </button>
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <button className="v2-affordance" onClick={onBack}>Back</button>
      </div>
    </div>
  );
}

function JournalRoom({ onBack }: { onBack: () => void }) {
  const { state } = useArc();
  const [items, setItems] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!state.projectId) { setLoading(false); return; }
    getAnnotations(state.projectId)
      .then((r) => setItems(r.annotations))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, [state.projectId]);

  async function save() {
    if (!state.projectId || !body.trim()) return;
    try {
      const saved = await postAnnotation({
        project_id: state.projectId,
        kind: "note",
        body,
        line_range: [0, 0],
        scene_idx: null,
        media_url: null,
      });
      setItems((prev) => [saved, ...prev]);
      setBody("");
      setHeld(true);
      window.setTimeout(() => setHeld(false), 1500);
    } catch { /* silent */ }
  }

  return (
    <div className="v2">
      <p className="v2-tracked-caps" style={{ textAlign: "center" }}>Journal</p>
      <h2 style={{ marginTop: 0, fontStyle: "italic", fontWeight: 300, textAlign: "center" }}>
        Your script & methodology.
      </h2>
      <hr className="v2-hairline" />

      <p className="v2-prompt">What do you want to remember?</p>
      <textarea
        className="v2-input"
        rows={4}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="…"
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.5rem" }}>
        <button className="v2-affordance" onClick={save} disabled={!body.trim()}>
          {held ? "Held" : "Hold"}
        </button>
      </div>

      <hr className="v2-hairline" />

      {loading && <p className="v2-stage-event">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="v2-prompt" style={{ color: "var(--v2-ink-faint)" }}>
          No entries yet. Write after the next rehearsal.
        </p>
      )}
      {items.map((a) => (
        <div key={a.id} style={{ padding: "0.7rem 0", borderBottom: "1px solid var(--v2-hairline)" }}>
          <p className="v2-tracked-caps" style={{ fontSize: "0.6rem" }}>
            {a.kind} · {new Date(a.ts_utc).toLocaleString()}
          </p>
          <p style={{ fontFamily: "var(--v2-serif)", margin: "0.2rem 0" }}>{a.body}</p>
        </div>
      ))}

      <p className="v2-prompt" style={{ fontSize: "0.85rem", color: "var(--v2-ink-faint)", textAlign: "center", marginTop: "1.5rem" }}>
        Private. Yours.
      </p>

      <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
        <button className="v2-affordance" onClick={onBack}>Back</button>
      </div>
    </div>
  );
}
